import React, { useState, useCallback } from "react";
import Box from "react-raster";
import Cropper from "react-easy-crop";
import styled from "styled-components";
import { Button, Text  } from "src/components";
import { useTranslation } from "react-i18next";
import { useDropzone } from "react-dropzone";

const AvatarUploadInner = styled.div`
  border:   2px dotted black;
  position: relative;
  display:  flex;
  width:    100%;
  height:   100%;
  padding:  1rem;
`;

const AvatarUploadForm = styled.div`
  justify-content: center;
  align-items:     center;
  display:         flex;
  flex-direction:  column;
  width:           100%;
  height:          100%;
  gap:             1rem;
`;

type ImageMedia = {
  height:        number;
  naturalHeight: number;
  naturalWidth:  number;
  width:         number;
}

type UploadProps = {
  onUploadComplete: (file: string | ArrayBuffer) => void;
  setMime: (mime: string) => void;
  onCancel: () => void;
};

const UploadAvatar = (props: UploadProps) => {
  const { t, i18n } = useTranslation();

  const onDrop = useCallback((acceptedFiles) => {
    acceptedFiles.forEach((file: any) => {
      const reader = new FileReader();
      reader.onabort = () => console.error("file reading was aborted");
      reader.onerror = () => console.error("file reading has failed");
      reader.onloadend = () => props.onUploadComplete(reader.result);
      props.setMime(file.type)
      reader.readAsDataURL(file);
    });
  }, []);
  const { getRootProps, getInputProps } = useDropzone({ onDrop });
  const { onCancel } = props;

  return (
    <AvatarUploadForm {...getRootProps()}>
      <input {...getInputProps()} />
      <Text size="small">{t("browse_or_drag_image")}</Text>
      <Text weight="bold" size="small">{t("OR")}</Text>
      <Button variant="primary" size="default">{t("browse")}</Button>
      {onCancel && <Button onClick = {(e) => {e.stopPropagation(); onCancel && onCancel();}} variant="outlined" size="default">{t("cancel")}</Button>}
    </AvatarUploadForm>
  );
};

type AvatarUploadProps = {
  onSave:    (uri: string|ArrayBuffer, size: number, mime: any) => void;
  onChange?: () => void;
  onCancel?: () => void;
};

export const AvatarUpload = (props: AvatarUploadProps) => {
  const [crop,  setCrop]  = useState({ x: 0, y: 0 });
  const [zoom,  setZoom]  = useState(1);
  const [image, setImage] = useState(null);
  const [mime,  setMime]  = useState(null);
  const [croppedImage,      setCroppedImage]      = useState(null);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
 
  const onCropComplete = useCallback((croppedArea, croppedAreaPixels) => {
    setCroppedAreaPixels(croppedAreaPixels);
  }, []);

  const { t, i18n } = useTranslation();

  const saveCroppedImage = async () => {
    try {
      const cropped = await getCroppedImg(image, croppedAreaPixels, 0, mime);
      const reader = new FileReader();
      reader.readAsArrayBuffer(cropped);
      reader.onloadend = async () => {

        const { size, type: mime }  = cropped;
        const { result: uri }       = reader;
     
        props.onSave(
          uri,
          size,
          mime
        );
      
      };
      setCroppedImage(cropped);
    } catch (e) {
      console.error(e);
    }
  }

  return (
    <Box
      display        = "flex"
      minHeight      = "25rem"
      justifyContent = "center"
      flexDirection  = "column"
    >
      <AvatarUploadInner>
        {!image && <UploadAvatar onCancel={props.onCancel} onUploadComplete={setImage} setMime={setMime} />}
        {image && (
          <Cropper
            image          = {image}
            crop           = {crop}
            zoom           = {zoom}
            aspect         = {4 / 4}
            onCropChange   = {setCrop}
            onCropComplete = {onCropComplete}
            onZoomChange   = {setZoom}
            style          = {{ containerStyle: { justifyContent: "center" } }}
          />
        )}
      </AvatarUploadInner>
      {image && (
        <Box marginTop="1rem" justifyContent='center' display='flex'>
          <Button size="default" onClick={saveCroppedImage}>
            {t("save_avatar")}
          </Button>
        </Box>
      )}
    </Box>
  );
};

const createImage = async (url: string): Promise<HTMLImageElement> => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.setAttribute("crossOrigin", "anonymous");
    img.onerror = reject;
    img.src = url;
  });
};

const getRadianAngle = (degreeValue: number) => {
  return (degreeValue * Math.PI) / 180;
};

/**
 * This function was adapted from the one in the ReadMe of https://github.com/DominicTobias/react-image-crop
 * @param {File} image - Image File url
 * @param {Object} pixelCrop - pixelCrop Object provided by react-easy-crop
 * @param {number} rotation - optional rotation parameter
 */
export default async function getCroppedImg(
  imageSrc,
  pixelCrop,
  rotation = 0,
  mime
): Promise<Blob> {
  const image = await createImage(imageSrc);
  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");

  const maxSize = Math.max(image.width, image.height);
  const safeArea = 2 * ((maxSize / 2) * Math.sqrt(2));

  // set each dimensions to double largest dimension to allow for a safe area for the
  // image to rotate in without being clipped by canvas context
  canvas.width = safeArea;
  canvas.height = safeArea;

  // translate canvas context to a central location on image to allow rotating around the center.
  ctx.translate(safeArea / 2, safeArea / 2);
  ctx.rotate(getRadianAngle(rotation));
  ctx.translate(-safeArea / 2, -safeArea / 2);

  // draw rotated image and store data.
  ctx.drawImage(
    image,
    safeArea / 2 - image.width * 0.5,
    safeArea / 2 - image.height * 0.5
  );

  const data = ctx.getImageData(0, 0, safeArea, safeArea);

  // set canvas width to final desired crop size - this will clear existing context
  canvas.width = pixelCrop.width;
  canvas.height = pixelCrop.height;

  // paste generated rotate image with correct offsets for x,y crop values.
  ctx.putImageData(
    data,
    Math.round(0 - safeArea / 2 + image.width * 0.5 - pixelCrop.x),
    Math.round(0 - safeArea / 2 + image.height * 0.5 - pixelCrop.y)
  );

  // As Base64 string
  // return canvas.toDataURL('image/jpeg');

  // As a blob
  return new Promise((resolve) => {
    canvas.toBlob((file) => {
      resolve(file);
    }, mime);
  });
}
